import { useApp } from '@/store/AppContext'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { formatCurrency } from '@/lib/pricing'
import {
  Car,
  Banknote,
  CreditCard,
  Lock,
  Unlock,
  Zap,
  Users,
  TrendingUp,
} from 'lucide-react'
import { isToday, differenceInMinutes } from 'date-fns'

function formatDuration(start: Date) {
  const diffMins = differenceInMinutes(new Date(), start)
  const h = Math.floor(diffMins / 60)
  const m = diffMins % 60
  if (h > 0) return `há ${h}h ${m}m`
  return `há ${m}m`
}

export function KPIHeader() {
  const {
    tickets,
    payments,
    settings,
    currentSession,
    isOperationMode,
    spaces,
    subscribers,
  } = useApp()

  const activeVehicles = tickets.filter((t) => t.status === 'active')
  const totalSpots = spaces.length || settings.totalSpots || 50
  const occupancyPercentage = Math.round(
    (activeVehicles.length / totalSpots) * 100,
  )

  const paymentsToday = payments.filter((p) => isToday(new Date(p.paidAt)))
  const revenueToday = paymentsToday.reduce((acc, p) => acc + p.amount, 0)
  const avgTicket =
    paymentsToday.length > 0 ? revenueToday / paymentsToday.length : 0

  const activeSubscribers = subscribers.filter((s) => s.status !== 'Suspenso')
  const fixedMonthlyRevenue = activeSubscribers.reduce(
    (acc, s) => acc + s.monthlyFee,
    0,
  )

  const startOfMonthDate = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    1,
  )
  const rotaryThisMonth = payments
    .filter((p) => new Date(p.paidAt) >= startOfMonthDate)
    .reduce((acc, p) => acc + p.amount, 0)
  const projectedMonthly = rotaryThisMonth + fixedMonthlyRevenue

  if (isOperationMode) {
    return (
      <div className="flex items-center justify-between bg-primary text-primary-foreground p-3 md:p-4 rounded-lg shadow-elevation">
        <div className="flex items-center gap-2 md:gap-3">
          <Zap className="h-5 w-5 md:h-6 md:w-6 text-yellow-400 fill-yellow-400" />
          <h2 className="text-lg md:text-xl font-bold">Modo Operação</h2>
        </div>
        <div className="text-right">
          <div className="font-bold text-xl md:text-2xl">
            {activeVehicles.length} veículos
          </div>
          <div className="text-xs md:text-sm opacity-80">
            {occupancyPercentage}% de ocupação
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3 md:space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4">
        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-1 p-3 md:pb-2 md:p-6 space-y-0">
            <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground truncate mr-2">
              Ocupação
            </CardTitle>
            <Car className="h-3.5 w-3.5 md:h-4 md:w-4 text-primary flex-shrink-0" />
          </CardHeader>
          <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
            <div className="text-lg md:text-2xl font-bold">
              {activeVehicles.length}{' '}
              <span className="text-[10px] md:text-base font-normal text-muted-foreground">
                / {totalSpots}
              </span>
            </div>
            <Progress
              value={occupancyPercentage}
              className="mt-2 md:mt-3 h-1.5 md:h-2"
            />
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-1 p-3 md:pb-2 md:p-6 space-y-0">
            <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground truncate mr-2">
              Rotativo (Hoje)
            </CardTitle>
            <Banknote className="h-3.5 w-3.5 md:h-4 md:w-4 text-emerald-600 flex-shrink-0" />
          </CardHeader>
          <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
            <div className="text-lg md:text-2xl font-bold">
              {formatCurrency(revenueToday)}
            </div>
            <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5 md:mt-1 truncate">
              {paymentsToday.length} transações
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-1 p-3 md:pb-2 md:p-6 space-y-0">
            <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground truncate mr-2">
              Ticket Médio
            </CardTitle>
            <CreditCard className="h-3.5 w-3.5 md:h-4 md:w-4 text-blue-600 flex-shrink-0" />
          </CardHeader>
          <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
            <div className="text-lg md:text-2xl font-bold">
              {formatCurrency(avgTicket)}
            </div>
            <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5 md:mt-1 truncate">
              Por finalizado
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-1 p-3 md:pb-2 md:p-6 space-y-0">
            <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground truncate mr-2">
              Caixa
            </CardTitle>
            {currentSession?.status === 'open' ? (
              <Unlock className="h-3.5 w-3.5 md:h-4 md:w-4 text-emerald-600 flex-shrink-0" />
            ) : (
              <Lock className="h-3.5 w-3.5 md:h-4 md:w-4 text-destructive flex-shrink-0" />
            )}
          </CardHeader>
          <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
            <div
              className={`text-lg md:text-2xl font-bold ${currentSession?.status === 'open' ? 'text-emerald-600' : 'text-destructive'}`}
            >
              {currentSession?.status === 'open' ? 'Aberto' : 'Fechado'}
            </div>
            <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5 md:mt-1 truncate">
              {currentSession?.status === 'open'
                ? formatDuration(currentSession.openedAt)
                : 'Fechado'}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4">
        <Card className="shadow-sm border-none bg-gradient-to-br from-primary/10 to-primary/5">
          <CardHeader className="flex flex-row items-center justify-between pb-1 p-3 md:pb-2 md:p-6 space-y-0">
            <CardTitle className="text-[11px] sm:text-xs md:text-sm font-bold text-primary truncate mr-2">
              Receita Diária
            </CardTitle>
            <Banknote className="h-3.5 w-3.5 md:h-4 md:w-4 text-primary flex-shrink-0" />
          </CardHeader>
          <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
            <div className="text-base sm:text-lg md:text-2xl font-black text-primary">
              {formatCurrency(revenueToday)}
            </div>
            <p className="hidden sm:block text-[10px] md:text-xs text-primary/70 mt-0.5 md:mt-1 truncate">
              Avulsos realizados hoje
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-none bg-gradient-to-br from-blue-500/10 to-blue-500/5">
          <CardHeader className="flex flex-row items-center justify-between pb-1 p-3 md:pb-2 md:p-6 space-y-0">
            <CardTitle className="text-[11px] sm:text-xs md:text-sm font-bold text-blue-700 truncate mr-2">
              Mensalidades
            </CardTitle>
            <Users className="h-3.5 w-3.5 md:h-4 md:w-4 text-blue-700 flex-shrink-0" />
          </CardHeader>
          <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
            <div className="text-base sm:text-lg md:text-2xl font-black text-blue-700">
              {formatCurrency(fixedMonthlyRevenue)}
            </div>
            <p className="hidden sm:block text-[10px] md:text-xs text-blue-700/70 mt-0.5 md:mt-1 truncate">
              {activeSubscribers.length} ativos
            </p>
          </CardContent>
        </Card>

        <Card className="col-span-2 md:col-span-1 shadow-sm border-none bg-gradient-to-br from-emerald-500/10 to-emerald-500/5">
          <CardHeader className="flex flex-row items-center justify-between pb-1 p-3 md:pb-2 md:p-6 space-y-0">
            <CardTitle className="text-xs md:text-sm font-bold text-emerald-700 truncate mr-2">
              Total Projetado do Mês
            </CardTitle>
            <TrendingUp className="h-3.5 w-3.5 md:h-4 md:w-4 text-emerald-700 flex-shrink-0" />
          </CardHeader>
          <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
            <div className="text-xl md:text-2xl font-black text-emerald-700">
              {formatCurrency(projectedMonthly)}
            </div>
            <p className="text-[10px] md:text-xs text-emerald-700/70 mt-0.5 md:mt-1 truncate">
              Rotativo acumulado + Mensalistas
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
